import { CountryStateCodes } from "@taabl/common-lib/constants/CountryStateCodes";
import { Formik } from "formik";
import moment from "moment";
import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikDropdown from "../../components/Forms/FormikDropdown";
import FormikTextField from "../../components/Forms/FormikTextField";
import LoaderButton from "../../components/LoaderButton";
import PageLoader from "../../components/PageLoader";
import photo1 from "../../images/benefit.svg";
import FormImage from "../../layouts/formimage";
import { Constants } from "../../libs/Constants";
import { update } from "../../libs/dbFunctions";
import { getCurrentDate, getValidDate } from "../../libs/utils";

const checkForNumbers = (inputString) => {
  if (typeof inputString === 'number') {
    return true;
  }
  const numbers = '1234567890';
  let hasOnlyNumber = true;
  for (let i = 0; i < inputString.length; i++) {
    if (!numbers.includes(inputString[i])) hasOnlyNumber = false;
  }
  return hasOnlyNumber;
}

const Address = (props) => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    address: "",
    city: "",
    zip: "",
    state: "",
    country: "USA",
    dateOfBirth: "",
    ssn: "",
    phoneNumber: "",
  });

  const schema = yup.object({
    address: yup.string().required("Address is required"),
    city: yup.string().required("City is required"),
    zip: yup
      .string()
      .required("Zip is required")
      .test("zip", function (value) {
        if (value && !checkForNumbers(value)) {
          return this.createError({
            message: "Please enter only numbers.",
            path: "zip",
          });
        }
        if (this.parent.country === "USA" && value && value.length !== 5) {
          return this.createError({
            message: "Zip should be 5 digits long.",
            path: "zip",
          });
        }
        return true;
      }),
    state: yup.string().required("State is required"),
    country: yup.string().required("Country is required"),
    dateOfBirth: yup
      .string()
      .required("Date Of Birth is required")
      .test("dateOfBirth", function (value) {
        let datevalidity = moment(
          value,
          [
            Constants.DATEFORMAT.MMDDYYYY,
            Constants.DATEFORMAT.MDDYYYY,
            Constants.DATEFORMAT.MMDYYYY,
            Constants.DATEFORMAT.MDYYYY,
          ],
          true
        );
        if (!datevalidity.isValid()) {
          return this.createError({
            message:
              "Please enter your Date Of Birth in the correct format (MM/DD/YYYY).",
            path: "dateOfBirth",
          });
        }
        let dob = getValidDate(value, false);
        let currentDate = getCurrentDate(false);
        if (currentDate.diff(dob, "years") < 18) {
          return this.createError({
            message: "User should be older than 18 years.",
            path: "dateOfBirth",
          });
        }
        if (currentDate.diff(dob, "years") > 125) {
          return this.createError({
            message: "User should be younger than 125 years.",
            path: "dateOfBirth",
          });
        }
        return true;
      }),
    ssn: yup
      .string()
      .required("Last four digits of the SSN are required.")
      .test("ssn", function (value) {
        if (value && !checkForNumbers(value)) {
          return this.createError({
            message: "Please enter only numbers.",
            path: "ssn",
          });
        }
        if (this.parent.country === "USA" && value && value.length !== 4) {
          return this.createError({
            message: "SSN should be 4 digits long.",
            path: "ssn",
          });
        }
        return true;
      }),
    phoneNumber: yup
      .string()
      .required("Phone number is required")
      .matches(/^[^|]/, "Invalid Phone Number")
      .test("phoneNumber", function (value) {
        if (value && !checkForNumbers(value)) {
          return this.createError({
            message: "Please enter only numbers.",
            path: "phoneNumber",
          });
        }
        if (this.parent.country === "USA" && value && value.length !== 10) {
          return this.createError({
            message:
              "Phone Number should be 10 digits long including country code.",
            path: "phoneNumber",
          });
        }
        return true;
      }),
  });

  const handleSubmit = async (values) => {
    let saveFields = {
      ...props.userObj,
      ...values,
      ["dateOfBirth"]: moment(
        values["dateOfBirth"],
        Constants.DATEFORMAT.MMDDYYYY
      ).format(Constants.DATEFORMAT.YYYYMMDD),
    };
    console.log(saveFields);
    //update the userObj with address details
    try {
      await update(Constants.dbTypes.USER, saveFields);
      await props.refreshUser();
      props.history.push("/");
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  };

  return (
    <PageLoader isActive={loading}>
      <FormImage
        title=""
        subtitle="Taabl will offset your payroll deduction with a risk free loan directly into your bank account. When the stock is issued, Taabl will help sell a portion to repay the loan and the 6% fee."
        image={photo1}
        bgClass="background-purple image-bg"
      >
        <React.Fragment>
          <h2 className="text-center pb-3">Personal Information</h2>
        </React.Fragment>

        <Formik
          validationSchema={schema}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          enableReinitialize
          validateOnMount
        >
          {({ handleSubmit, isValid, isSubmitting, values, setFieldValue }) => (
            <form noValidate onSubmit={handleSubmit} className="w-100">
              <FormikDropdown
                options={CountryStateCodes}
                placeholder="Select Country"
                size="lg"
                controlId="country"
                label="Country"
                type="text"
                name="country"
                className="mb-4"
                optionVal="iso3"
              />
              <FormikDropdown
                size="lg"
                options={
                  values.country !== "" && values.country !== "Select Country"
                    ? CountryStateCodes.filter(
                      (c) => c.iso3 === values.country
                    )[0].states
                    : []
                }
                placeholder="Select State"
                controlId="state"
                label="State"
                type="text"
                name="state"
                className="mb-4"
                optionVal="state_code"
              />

              <FormikTextField
                size="lg"
                controlId="city"
                label="City"
                type="text"
                name="city"
                className="mb-4"
              />
              <FormikTextField
                size="lg"
                controlId="address"
                label="Address"
                type="text"
                name="address"
                className="mb-4"
              />
              <FormikTextField
                size="lg"
                controlId="zip"
                label="Zip"
                type="text"
                name="zip"
                className="mb-4"
              />
              <FormikTextField
                size="lg"
                controlId="dateOfBirth"
                label="Date Of Birth"
                type="text"
                name="dateOfBirth"
                className="mb-4"
                placeholder={Constants.DATEFORMAT.MMDDYYYY}
              />
              <FormikTextField
                size="lg"
                controlId="ssn"
                label="SSN (Last four digits)"
                type="text"
                name="ssn"
                className="mb-4"
                labelAppend={
                  <OverlayTrigger
                    placement={"top"}
                    delay={{ show: 150, hide: 250 }}
                    overlay={
                      <Tooltip style={{ zIndex: 9999999999 }}>
                        The last four is required by federal law to protect your
                        identity and ensure this account is being opened by an
                        authorized user.
                      </Tooltip>
                    }
                  >
                    <FaInfoCircle
                      style={{
                        marginLeft: "5px",
                      }}
                    />
                  </OverlayTrigger>
                }
              />
              <FormikTextField
                size="lg"
                controlId="phoneNumber"
                label="Phone number"
                type="text"
                name="phoneNumber"
                className="mb-4"
              />
              <LoaderButton
                block
                type="submit"
                variant="primary"
                size="lg"
                text="Next"
                className="mb-2"
                isLoading={isSubmitting}
                disabled={!isValid || isSubmitting}
              >
                Next
              </LoaderButton>
            </form>
          )}
        </Formik>
      </FormImage>
    </PageLoader>
  );
};

export default Address;
